import { useMutation } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { type SsoConnection, type SsoDomain, ssoCreateDomainMutation, ssoVerifyDomainMutation } from '@column/api';
import { Alert, AlertDescription, Button, Input } from '@column/ui';

import { getErrorMessage } from '~/lib/api/getErrorMessage';
import { useNotificationStore } from '~/stores/Notification';
import { formatDateTime } from '~/util';

import { DomainRecord, DomainStep } from './DomainStep';
import { isStepUpError } from './utils';

interface UseDomainPanelOptions {
  connection?: SsoConnection;
  domainId?: string;
  isActive: boolean;
  onBack: () => void;
  onChanged: () => Promise<unknown>;
  onCreated: (domainId: string) => void;
  onRemove: (domain: SsoDomain) => void;
  isRemoving: boolean;
}

export const useDomainPanel = ({
  connection,
  domainId,
  isActive,
  onBack,
  onChanged,
  onCreated,
  onRemove,
  isRemoving,
}: UseDomainPanelOptions) => {
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const domain = connection?.domains.find((item) => item.id === domainId);
  const [newDomain, setNewDomain] = useState('');
  const createDomain = useMutation(ssoCreateDomainMutation());
  const verifyDomain = useMutation(ssoVerifyDomainMutation());
  const [isRunning, setIsRunning] = useState(false);
  const isPending = isRunning || createDomain.isPending || verifyDomain.isPending || isRemoving;

  useEffect(() => {
    if (!isActive) return;
    setNewDomain('');
  }, [isActive]);

  const run = async (action: () => Promise<unknown>, success: string): Promise<boolean> => {
    setIsRunning(true);
    try {
      await action();
      await onChanged();
      addSuccessNotification({ content: success });
      return true;
    } catch (error) {
      if (!isStepUpError(error)) {
        addDangerNotification({ content: getErrorMessage(error) });
      }
      return false;
    } finally {
      setIsRunning(false);
    }
  };

  const handleAdd = async () => {
    const name = newDomain.trim();
    if (!connection || !name) return;
    let createdId: string | undefined;
    const added = await run(async () => {
      createdId = (
        await createDomain.mutateAsync({ path: { sso_connection_id: connection.id }, body: { domain: name } })
      ).id;
    }, `${name} added`);
    if (added && createdId) onCreated(createdId);
  };

  const handleVerify = async () => {
    if (!connection || !domain) return;
    await run(
      () => verifyDomain.mutateAsync({ path: { sso_connection_id: connection.id, sso_domain_id: domain.id } }),
      `${domain.domain} verified`
    );
  };

  const body = (
    <div className="grid gap-6 px-6 pt-6">
      <Alert>
        <Info />
        <AlertDescription>Enable users with email addresses on this domain to use single sign-on.</AlertDescription>
      </Alert>
      <div className="grid">
        <DomainStep
          title="Enter the domain"
          description="The email domain your team signs in with."
          state={domain ? 'done' : 'current'}
        >
          <div className="flex items-end gap-3 px-4 py-3">
            <div className="grid min-w-0 flex-1 gap-1">
              <span className="text-xs text-muted-foreground">Domain</span>
              <Input
                inputSize="sm"
                autoFocus={!domain}
                readOnly={!!domain}
                value={domain ? domain.domain : newDomain}
                onChange={(event) => setNewDomain(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void handleAdd();
                  }
                }}
                placeholder="example.com"
                aria-label="Domain"
                disabled={isPending}
              />
            </div>
            {!domain && (
              <Button
                size="sm"
                type="button"
                onClick={() => void handleAdd()}
                disabled={!newDomain.trim() || isPending}
              >
                Continue
              </Button>
            )}
          </div>
        </DomainStep>
        <DomainStep
          title="Add TXT record"
          description="Publish this record with your DNS provider."
          state={!domain ? 'upcoming' : domain.verified_at ? 'done' : 'current'}
        >
          {domain && (
            <>
              <DomainRecord label="Record" value={domain.txt_record_name} />
              <DomainRecord label="Value" value={domain.txt_record_value} />
            </>
          )}
        </DomainStep>
        <DomainStep
          title="Verify domain ownership"
          description={
            domain?.verified_at
              ? `Verified ${formatDateTime(new Date(domain.verified_at), { timeZoneFormat: 'local' })}.`
              : 'DNS changes can take a few minutes to propagate.'
          }
          state={domain?.verified_at ? 'done' : domain ? 'current' : 'upcoming'}
          isLast
        >
          {domain && !domain.verified_at && (
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <span className="text-sm text-muted-foreground">Check for the TXT record now.</span>
              <Button size="sm" type="button" onClick={() => void handleVerify()} disabled={isPending}>
                Verify
              </Button>
            </div>
          )}
        </DomainStep>
      </div>
    </div>
  );

  const footer = (
    <>
      <Button variant="secondary" size="sm" type="button" onClick={onBack} disabled={isPending}>
        Back
      </Button>
      {domain && (
        <Button
          variant="secondary-danger"
          size="sm"
          type="button"
          onClick={() => onRemove(domain)}
          disabled={isPending}
        >
          Remove
        </Button>
      )}
    </>
  );

  return { body, footer, domain, isPending };
};
